import "./broadcast.css";
import { parseServerMessage, PROTOCOL_VERSION } from "../network/protocol";
import { NetworkBattleSpectatorClient } from "../network/NetworkBattleSpectatorClient";
import { BroadcastPolicy, BroadcastReadinessWatchdog, BroadcastTransitionQueue, broadcastViewUrl, parseBroadcastOptions, type BroadcastView } from "./policy";

const query = new URLSearchParams(location.search);
const battleSession = query.get("battleSession");
let battleClient: NetworkBattleSpectatorClient | null = null;
const options = parseBroadcastOptions(query);
const policy = new BroadcastPolicy(options.views, options.dwellMs, Date.now());
const queue = new BroadcastTransitionQueue();
const liveReadiness = new BroadcastReadinessWatchdog();
const boot = Date.now().toString(36);
const stage = document.querySelector<HTMLElement>("#broadcast-stage")!;
const statusNotice = document.createElement("p"); statusNotice.className = "broadcast-status";
statusNotice.setAttribute("role", "status"); statusNotice.hidden = true; stage.append(statusNotice);
let battleTerminalError = "";
const background = query.get("bg");
if (background && background.length <= 100 && CSS.supports("background-color", background)) stage.style.backgroundColor = background;
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
const LOAD_TIMEOUT_MS = 8_000;
const WARM_MS = 2_600;
const FADE_MS = 600;

interface Slot {
  frame: HTMLIFrameElement;
  view: BroadcastView | null;
  epoch: number;
  ready: boolean;
}
const slots: Slot[] = Array.from({ length: 2 }, (_, index) => {
  const frame = document.createElement("iframe");
  frame.className = "broadcast-slot";
  frame.title = `BeerKart spectator view ${index + 1}`;
  frame.tabIndex = -1;
  frame.setAttribute("aria-hidden", "true");
  frame.referrerPolicy = "same-origin";
  frame.src = "about:blank";
  stage.append(frame);
  return { frame, view: null, epoch: 0, ready: false };
});
let liveIndex: number | null = null;
let operation: AbortController | null = null;
let stopped = false;
let hidden = document.hidden;
let failedView: BroadcastView | null = null;
let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let reconnectDelay = 1_000;
let phase: string | null = null;
let wsHealthy = false;
const failures: Array<{ at: number; view: BroadcastView; reason: string }> = [];

function recordFailure(view: BroadcastView, reason: string): void {
  failedView = view;
  policy.fail(Date.now());
  failures.push({ at: Date.now(), view, reason });
  if (failures.length > 20) failures.shift();
}

function park(slot: Slot): void {
  slot.epoch += 1;
  slot.ready = false;
  slot.view = null;
  slot.frame.style.visibility = "hidden";
  slot.frame.style.opacity = "0";
  // Spectators have no local race authority. Discarding the hidden document
  // releases its render loops, socket and GPU context as a single lifecycle.
  // A revisit waits for fresh snapshot/render readiness before becoming live.
  slot.frame.src = "about:blank";
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolveWait, rejectWait) => {
    if (signal.aborted) { rejectWait(new Error("Broadcast transition cancelled")); return; }
    const abort = (): void => { clearTimeout(timer); rejectWait(new Error("Broadcast transition cancelled")); };
    const timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolveWait(); }, ms);
    signal.addEventListener("abort", abort, { once: true });
  });
}

function frameState(slot: Slot): Record<string, unknown> | null {
  try {
    const child = slot.frame.contentWindow as (Window & { render_game_to_text?: () => string }) | null;
    if (!child?.render_game_to_text) return null;
    const value: unknown = JSON.parse(child.render_game_to_text());
    return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
  } catch { return null; }
}

function rendererReady(slot: Slot): boolean {
  try {
    const child = slot.frame.contentWindow as (Window & { __beerkartBroadcastReady?: boolean }) | null;
    return child?.location.href === slot.frame.src && child.__beerkartBroadcastReady === true;
  } catch { return false; }
}

async function transition(view: BroadcastView): Promise<void> {
  const controller = new AbortController();
  operation = controller;
  const index = liveIndex === 0 ? 1 : 0;
  const incoming = slots[index];
  park(incoming);
  const epoch = incoming.epoch;
  incoming.view = view;
  incoming.frame.style.visibility = "visible";
  incoming.frame.style.opacity = "0";
  incoming.frame.src = broadcastViewUrl(view, boot, battleSession);
  let success = false;
  try {
    const deadline = performance.now() + LOAD_TIMEOUT_MS;
    while (!rendererReady(incoming)) {
      if (performance.now() >= deadline) throw new Error("Renderer readiness timed out");
      await wait(50, controller.signal);
      if (incoming.epoch !== epoch) throw new Error("Spectator slot replaced");
    }
    incoming.ready = true;
    // The readiness timeout ends here, not during a healthy warm/fade window.
    await wait(WARM_MS, controller.signal);
    if (!rendererReady(incoming)) throw new Error("Renderer lost readiness while warming");
    incoming.frame.style.opacity = "1";
    if (liveIndex !== null) slots[liveIndex].frame.style.opacity = "0";
    await wait(reducedMotion.matches ? 0 : FADE_MS + 60, controller.signal);
    if (liveIndex !== null) park(slots[liveIndex]);
    liveIndex = index;
    liveReadiness.reset();
    success = true;
  } catch (error) {
    if (!controller.signal.aborted) {
      recordFailure(view, error instanceof Error ? error.message : "Spectator view failed");
      // Keep the prior live document visible. A broken target never replaces it.
      if (liveIndex !== null) slots[liveIndex].frame.style.opacity = "1";
      queue.request("2d");
    }
    if (incoming.epoch === epoch) park(incoming);
  } finally {
    if (operation !== controller) return;
    operation = null;
    if (controller.signal.aborted) return;
    const next = queue.settle(success);
    if (next !== null && !hidden && !stopped) void transition(next);
  }
}

function refresh(): void {
  if (hidden || stopped) return;
  statusNotice.textContent = battleTerminalError || (liveIndex === null && failedView && policy.degraded(Date.now())
    ? "SPECTATOR VIEW UNAVAILABLE · RETRYING…" : battleSession !== null && !wsHealthy ? "BATTLE FEED · RECONNECTING…" : "");
  statusNotice.hidden = !statusNotice.textContent;
  if (battleTerminalError) return;
  const now = Date.now();
  if (liveIndex !== null) {
    const live = slots[liveIndex];
    const ready = rendererReady(live);
    if (live.view && liveReadiness.check(ready, now)) {
      recordFailure(live.view, "Live renderer remained unready");
      // A failed 2D view is retried after backoff in the other slot; retain its
      // last pixels until a replacement is ready instead of blanking the source.
      if (queue.live === "2d") queue.live = null;
    } else if (ready && queue.live === null && queue.target === null) queue.live = live.view;
  }
  const desired = policy.desired(now);
  // If even 2D failed, honour the same retry backoff instead of loading it on
  // every snapshot. A different, healthy fallback may still be requested.
  if (policy.degraded(now) && desired === failedView) return;
  const next = queue.request(desired);
  if (next !== null) void transition(next);
}

function connect(): void {
  if (battleSession !== null) {
    if (stopped || hidden || battleClient || battleTerminalError) return;
    try {
      battleClient = new NetworkBattleSpectatorClient(battleSession);
      battleClient.onError = error => {
        if (!error.fatal) return;
        battleTerminalError = `BATTLE UNAVAILABLE · ${error.message}`;
        wsHealthy = false; phase = "unavailable"; parkAll(); refresh();
      };
      battleClient.onStateChange = () => {
        if (battleTerminalError) { wsHealthy = false; phase = "unavailable"; refresh(); return; }
        wsHealthy = battleClient?.connected ?? false;
        phase = battleClient?.snapshot?.phase ?? null;
        if (wsHealthy && phase) policy.observe(phase === "active" ? "race" : phase, Date.now());
        else policy.disconnected(Date.now());
        refresh();
      };
      battleClient.connect();
    } catch { phase = "invalid-battle-session"; wsHealthy = false; battleTerminalError = "BATTLE UNAVAILABLE · A valid battleSession is required."; parkAll(); refresh(); }
    return;
  }
  if (stopped || (socket && socket.readyState < WebSocket.CLOSING)) return;
  const ws = new WebSocket(`${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/ws`);
  socket = ws;
  ws.addEventListener("open", () => {
    if (socket !== ws || stopped) return;
    ws.send(JSON.stringify({ type: "hello", protocol: PROTOCOL_VERSION, role: "overlay" }));
  });
  ws.addEventListener("message", event => {
    if (socket !== ws || stopped || typeof event.data !== "string") return;
    const message = parseServerMessage(event.data);
    if (message?.type !== "snapshot") return;
    phase = message.state.phase;
    wsHealthy = true;
    reconnectDelay = 1_000;
    policy.observe(phase, Date.now());
    refresh();
  });
  ws.addEventListener("close", () => {
    if (socket !== ws) return;
    socket = null;
    wsHealthy = false;
    policy.disconnected(Date.now());
    if (!stopped && reconnectTimer === null) {
      reconnectTimer = window.setTimeout(() => { reconnectTimer = null; connect(); }, reconnectDelay);
      reconnectDelay = Math.min(15_000, reconnectDelay * 2);
    }
    refresh();
  });
}

function parkAll(): void {
  operation?.abort();
  operation = null;
  for (const slot of slots) park(slot);
  liveIndex = null;
  liveReadiness.reset();
  queue.reset();
}

document.addEventListener("visibilitychange", () => {
  hidden = document.hidden;
  if (hidden) { parkAll(); const retiring = battleClient; battleClient = null; if (retiring) { retiring.onStateChange = null; retiring.onError = null; retiring.close(); } if (battleSession !== null) { wsHealthy = false; phase = null; } }
  else { connect(); refresh(); }
});
window.addEventListener("pagehide", () => {
  stopped = true;
  parkAll();
  if (reconnectTimer !== null) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  socket?.close();
  if (battleClient) { battleClient.onStateChange = null; battleClient.onError = null; battleClient.close(); battleClient = null; }
});
window.addEventListener("pageshow", () => { stopped = false; hidden = document.hidden; connect(); refresh(); });

function diagnostics() {
  return { mode: "broadcast", readOnly: true, battleSession, views: options.views, dwellMs: options.dwellMs,
    phase, wsHealthy, battleTerminalError, degraded: policy.degraded(Date.now()), rotating: !battleTerminalError && policy.rotating(Date.now()),
    liveView: queue.live, target: queue.target, pending: queue.pending, hidden,
    liveUrl: liveIndex === null ? null : slots[liveIndex].frame.getAttribute("src"),
    resourcePolicy: "two slots; hidden documents unloaded after fade", failures: [...failures],
    slots: slots.map((slot, index) => ({ view: slot.view, live: index === liveIndex,
      active: slot.view !== null, ready: slot.ready, visibility: slot.frame.style.visibility,
      opacity: slot.frame.style.opacity, url: slot.frame.getAttribute("src"), footprint: frameState(slot)?.render ?? null })),
  };
}
Object.defineProperty(window, "__beerkartBroadcast", { get: diagnostics });
window.render_game_to_text = () => JSON.stringify(diagnostics());
window.setInterval(refresh, 250);
connect();
refresh();
